import React, { useMemo, useState } from "react";
import clsx from "clsx";
import AdSlot, { AdItem } from "@/components/ads/AdSlot";

interface AdsCarouselProps {
  items: AdItem[];
  className?: string;
  slideIntervalMs?: number; // auto-play interval
  showDots?: boolean;
  showArrows?: boolean;
  rounded?: boolean;
}

/**
 * AdsCarousel - Komponen iklan slideable ala carousel
 * - Tanpa dependensi eksternal
 * - Auto-play + manual navigation
 */
const AdsCarousel: React.FC<AdsCarouselProps> = ({
  items = [],
  className = "",
  slideIntervalMs = 5000,
  showDots = true,
  showArrows = true,
  rounded = true,
}) => {
  const [index, setIndex] = useState(0);

  const safeItems = useMemo(() => items.filter(Boolean), [items]);
  const total = safeItems.length;

  React.useEffect(() => {
    if (total <= 1) return; // no autoplay if only 1
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, slideIntervalMs);
    return () => clearInterval(timer);
  }, [total, slideIntervalMs]);

  if (total === 0) return null;

  const goPrev = () => setIndex((prev) => (prev - 1 + total) % total);
  const goNext = () => setIndex((prev) => (prev + 1) % total);

  return (
    <div className={clsx("relative w-full", className)}>
      <div className="overflow-hidden rounded-lg">
        {safeItems.map((item, i) => (
          <div
            key={item.id ?? i}
            className={clsx(
              "transition-opacity duration-700",
              i === index ? "opacity-100" : "opacity-0 absolute inset-0"
            )}
          >
            <AdSlot {...item} rounded={rounded} />
          </div>
        ))}
      </div>

      {showArrows && total > 1 && (
        <>
          <button
            type="button"
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-black/30 hover:bg-white shadow rounded-full w-8 h-8 flex items-center justify-center"
            onClick={goPrev}
            aria-label="Previous ad"
          >
            ‹
          </button>
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-black/30 hover:bg-white shadow rounded-full w-8 h-8 flex items-center justify-center"
            onClick={goNext}
            aria-label="Next ad"
          >
            ›
          </button>
        </>
      )}

      {showDots && total > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
          {safeItems.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={clsx(
                "w-2.5 h-2.5 rounded-full",
                i === index ? "bg-white" : "bg-white/50"
              )}
              aria-label={`Go to ad ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdsCarousel;
export type { AdsCarouselProps };

